'use client'

import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '@/contexts/AuthContext'

const API_URL = process.env.NEXT_PUBLIC_API_URL

export type UserRole = 'regular' | 'beta' | 'admin'

let cachedRole: { role: UserRole; at: number } | null = null
const CACHE_TTL = 300_000 // 5 min

export function useAdminRole() {
  const { user } = useAuth()
  const [role, setRole] = useState<UserRole>(cachedRole?.role ?? 'regular')
  const [isLoading, setIsLoading] = useState(!cachedRole)

  const fetchRole = useCallback(async () => {
    if (!user) {
      setRole('regular')
      setIsLoading(false)
      return
    }
    if (cachedRole && Date.now() - cachedRole.at < CACHE_TTL) {
      setRole(cachedRole.role)
      setIsLoading(false)
      return
    }
    try {
      const res = await fetch(`${API_URL}/v1/admin/me/role`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        const r = data.role as UserRole
        cachedRole = { role: r, at: Date.now() }
        setRole(r)
      }
    } catch {
      // ignore
    } finally {
      setIsLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchRole()
  }, [fetchRole])

  return { role, isAdmin: role === 'admin', isBeta: role === 'beta', isLoading }
}
